import { Flag, AlertTriangle } from "lucide-react";
import { FIELD_KIND_LABEL } from "./crmShared";

/** Source unique du rendu « indicatif · hors EDD » (fiche lot, popover survol, tuile récap). */
export const INDICATIF = {
  red: "#DC2626", label: "#f4a4a4", unit: "rgba(220,38,38,.55)",
  tile: { background: "linear-gradient(135deg, rgba(220,38,38,.05), rgba(220,38,38,.01))", border: "0.5px solid rgba(220,38,38,.4)" },
  chip: { fontSize: 8, padding: "1px 5px", borderRadius: 3, background: "rgba(220,38,38,.14)", border: "0.5px solid rgba(220,38,38,.35)", color: "#f4a4a4", letterSpacing: ".3px", textTransform: "none", whiteSpace: "nowrap" },
};

/** Libellé indicatif : drapeau 10 px #DC2626 + libellé #f4a4a4 + chip « hors EDD ». */
export function IndicatifLabel({ label, size = 10, chip = true, testKey, style }) {
  return (
    <span className="inline-flex items-center" style={{ gap: 4, minWidth: 0, color: INDICATIF.label, ...style }} data-indicatif="true">
      <Flag size={size} style={{ color: INDICATIF.red, flexShrink: 0 }} data-testid={testKey ? `lot-indicatif-flag-${testKey}` : undefined} />
      <span className="truncate">{label}</span>
      {chip && <span style={INDICATIF.chip} data-testid={testKey ? `lot-hors-edd-${testKey}` : undefined}>hors EDD</span>}
    </span>
  );
}

/** Valeur indicative : nombre en #DC2626 weight 500, unité en rgba(220,38,38,.55). */
export function IndicatifValue({ value, unit = "m²", size = 12, unitSize = 9, testKey }) {
  return (
    <span className="tabular-nums" style={{ color: INDICATIF.red, fontWeight: 500, fontSize: size }} data-indicatif="true" data-testid={testKey ? `lot-value-${testKey}` : undefined}>
      {value} <span style={{ fontSize: unitSize, color: INDICATIF.unit, fontWeight: 400 }}>{unit}</span>
    </span>
  );
}

/** Ligne « Libellés existants » : rendu rouge si le libellé est indicatif dans au moins un type (flag catalogue). */
export function CatalogRow({ c, Icon, count = 0, onClick, testId, badgeTestId, title }) {
  const ind = !!c.indicatif;
  return (
    <button type="button" onClick={onClick} className={`w-full flex items-center ${ind ? "catalog-row-indicatif" : "hover:bg-[#1e1a2e]"}`} title={title || (ind ? `Indicatif · hors EDD${c.indicatif_in?.length ? ` (marqué dans : ${c.indicatif_in.join(", ")})` : ""}` : "")}
      style={{ gap: 8, padding: "5px 6px", borderRadius: 6, fontSize: 11.5, color: ind ? INDICATIF.label : "#e8e8ec", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }} data-testid={testId} data-indicatif={ind || undefined}>
      {ind ? <Flag size={12} style={{ color: INDICATIF.red, flexShrink: 0 }} /> : <Icon size={12} style={{ color: "#a8a6e8", flexShrink: 0 }} />}
      <span style={{ flex: 1 }}>{c.label}</span>
      {count > 0 && <span style={{ fontSize: 9, background: "#16241c", color: "#7fd6ab", borderRadius: 8, padding: "1px 5px" }} data-testid={badgeTestId}>×{count} déjà</span>}
      <span style={{ fontSize: 9.5, color: ind ? INDICATIF.unit : "#6e6e78" }}>{FIELD_KIND_LABEL[c.kind] || c.kind}</span>
    </button>
  );
}

/** Chip ambre « champ orphelin » (clé absente de tous les types). */
export function OrphanChip({ testKey }) {
  return <span className="inline-flex items-center" style={{ gap: 3, fontSize: 8, textTransform: "uppercase", letterSpacing: ".4px", background: "rgba(217,155,30,.14)", border: "0.5px solid rgba(217,155,30,.4)", color: "#e6c383", borderRadius: 3, padding: "1px 5px", cursor: "help", whiteSpace: "nowrap" }}
    title="Ce champ n'existe plus dans la configuration du type parent. Il est conservé sur ce lot mais peut être supprimé sans impact sur les autres lots. Contacter un administrateur pour régularisation." data-testid={testKey ? `lot-orphan-${testKey}` : undefined}><AlertTriangle size={9} />champ orphelin</span>;
}

/** Nom court d'un champ indicatif pour les bandeaux : « Surface réelle » → « Surf. réelle ». */
export const shortIndicatifLabel = (label) => String(label || "").replace(/^Surface\s+/i, "Surf. ").replace(/^Surf\.\s+Surf\.\s+/i, "Surf. ");

/** Bandeau « hors EDD » en pied de tuile (spec iter145) : dégradé rouge, pleine largeur, une ligne par champ indicatif.
 *  `inset` = [x, y] du padding du conteneur pour déborder jusqu'aux bords ; `compact` = variante popover. */
export function HorsEddBanner({ rows = [], inset = [11, 10], compact = false, radius = 8, testId = "hors-edd-banner" }) {
  const list = rows.filter((r) => r && r.value !== "" && r.value != null);
  if (!list.length) return null;
  const fs = compact ? 9.5 : 10.5, icon = compact ? 9 : 11, chip = compact ? 7 : 8;
  const pad = compact ? "5px 9px" : list.length > 1 ? "5px 11px" : "7px 11px";
  return (
    <div className="flex flex-col" style={{ margin: `6px -${inset[0]}px -${inset[1]}px`, marginTop: "auto", paddingTop: 6, alignSelf: "stretch" }} data-testid={testId} data-count={list.length}>
      <div className="flex flex-col" style={{ gap: 3, padding: pad, background: "linear-gradient(90deg, rgba(220,38,38,.1), rgba(220,38,38,.02))", borderTop: "0.5px solid rgba(220,38,38,.25)", borderRadius: `0 0 ${radius}px ${radius}px` }}>
        {list.map((r) => (
          <div key={r.key} className="flex items-center" style={{ gap: 5, minWidth: 0 }} data-testid={`${testId}-row-${r.key}`} data-indicatif="">
            <Flag size={icon} style={{ color: "#DC2626", flexShrink: 0 }} />
            <span className="truncate" style={{ fontSize: fs, color: "#f4a4a4", minWidth: 0 }} title={r.label}>{shortIndicatifLabel(r.label)}</span>
            <span className="tabular-nums" style={{ marginLeft: "auto", fontSize: fs, fontWeight: 500, color: "#ff6b6b", whiteSpace: "nowrap" }}>{r.value}</span>
            <span style={{ fontSize: 9, color: "rgba(255,107,107,.7)", marginLeft: 1 }}>{r.unit || "m²"}</span>
            <span style={{ fontSize: chip, letterSpacing: ".4px", textTransform: "uppercase", color: "rgba(244,164,164,.7)", marginLeft: 5, whiteSpace: "nowrap" }}>hors EDD</span>
          </div>
        ))}
      </div>
    </div>
  );
}
